"use client";

import { memo, useEffect, useState } from "react";

import { ButtonLink } from "../ui/Button/Button";
import Image from "next/image";
import styles from "./WhatWeOffer.module.css";
import { useTranslations } from "next-intl";

interface WhatWeOfferProps {
  locale?: string;
  className?: string;
}

export const WhatWeOffer: React.FC<WhatWeOfferProps> = memo(
  ({ locale = "en", className }) => {
    const t = useTranslations("whatWeOffer");
    const isRTL = locale === "ar";
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      checkMobile();
      window.addEventListener("resize", checkMobile);

      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const backgroundImage = isRTL
      ? "/images/what-we-offer/Services-BG-AR.webp"
      : "/images/what-we-offer/Services-BG-EN.webp";

    const renderContent = () => {
      const textContent = (
        <div className={styles.textContent} dir={isRTL ? "rtl" : "ltr"}>
          <span className={styles.label}>{t("label")}</span>
          <h3>
            {t("appTitle.line1")}
            <br />
            {t("appTitle.line2")}
            <br />
            {t("appTitle.line3")}
          </h3>
          <p>{t("description")}</p>

          <ButtonLink
            outline="outline"
            variant="secondary"
            href={`${locale}/mobile-app`}
            aria-label={t("learnMore")}
          >
            {t("learnMore")}
          </ButtonLink>
        </div>
      );

      const imageContent = (
        <div className={styles.imageContent}>
          <div className={styles.imageWrapper}>
            <Image
              src="/images/what-we-offer/floating-mobile.webp"
              alt={t("mobileAppAlt")}
              width={isMobile ? 280 : 400}
              height={isMobile ? 560 : 800}
              priority
              quality={90}
              className={styles.mobileApp}
            />
          </div>
        </div>
      );

      return (
        <div className={styles.content} dir={isRTL ? "rtl" : "ltr"}>
          {isRTL ? (
            <>
              {imageContent}
              {textContent}
            </>
          ) : (
            <>
              {textContent}
              {imageContent}
            </>
          )}
        </div>
      );
    };

    return (
      <section
        className={`${styles.wrapper} ${className || ""}`}
        dir={isRTL ? "rtl" : "ltr"}
        lang={locale}
        aria-labelledby="what-we-offer-title"
      >
        <Image
          src={backgroundImage}
          alt=""
          fill
          priority
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
          quality={90}
          className={styles.backgroundImage}
        />

        <div className={styles.container}>
          <div className={styles.header}>
            <h2 id="what-we-offer-title">{t("title")}</h2>
            <p>{t("subtitle")}</p>
          </div>

          {renderContent()}
        </div>
      </section>
    );
  }
);

WhatWeOffer.displayName = "WhatWeOffer";

export default WhatWeOffer;
